import type { Bonus, CollageItem, FaqItem, Feature, Place, Printable, Step, Testimonial } from '../types/content'

export const productName = 'Pack Complet d’Évangélisation'
export const checkoutUrl = 'https://the-gospel-sharing-kit.impultienda.ar/checkout'
export const offerAnchor = '#offer'

/** Finished main product mockup — used once, in the hero. */
export const heroMockup = 'https://cdn.magicpatterns.com/uploads/2vj8c9qfY99QBqGmGnpJJc/MOCKUP_(2).png'

/* ---------- Section 2 — Conviction ---------- */
export const hesitations = [
  'Qu’est-ce que je vais dire ?',
  'Et s’ils me rejettent ?',
  'Je suis trop timide.',
  'Je ne connais pas assez la Bible.',
  'Je ne veux pas créer de malaise.',
  'Je le ferai la prochaine fois.',
]

/* ---------- Section 3 — Solution ---------- */
export const simpleMessages = [
  'Un verset biblique.',
  'Un message d’espérance.',
  'Un rappel de l’amour de Dieu.',
  'Un message simple qui oriente quelqu’un vers Jésus.',
]
export const handoffs = [
  'le remettre à quelqu’un…',
  'le glisser dans un cadeau…',
  'le partager avec un collègue…',
  'l’offrir à un voisin…',
  'ou le garder sur vous pour être prêt quand une occasion se présente.',
]
export const noMores = [
  'Pas de préparation compliquée.',
  'Plus besoin de chercher quoi créer.',
  'Plus besoin d’attendre d’avoir suffisamment confiance en vous pour commencer.',
]

/* ---------- Section 4 — How it works ---------- */
export const steps: Step[] = [
  {
    title: 'Imprimez',
    text: 'Choisissez votre verset ou votre modèle préféré, puis imprimez-le chez vous ou dans un service d’impression.',
  },
  {
    title: 'Découpez',
    text: 'Découpez les cartes, les petits mots ou les messages imprimables que vous souhaitez partager.',
    extras: ['Rien à concevoir de zéro.', 'Aucune installation compliquée.', 'Tout est déjà prêt pour vous.'],
  },
  {
    title: 'Partagez',
    text: 'Gardez-les sur vous et utilisez-les chaque fois que Dieu vous donne l’occasion de semer une graine.',
    extras: ['Un petit message peut toucher quelqu’un au moment précis où il en a besoin.'],
  },
]

/* ---------- Section 5 — Material preview ---------- */
export const printables: Printable[] = [
  {
    id: 'butterfly',
    src: 'https://cdn.magicpatterns.com/uploads/2ZnKpKVVcSnXx5Y4tPsfzE/Captura_de_pantalla_2026-10-01_104603.png',
    alt: 'Carte papillon bleu « Un message de Dieu pour vous » avec 1 John 4:10',
    wide: false,
  },
  {
    id: 'roses',
    src: 'https://cdn.magicpatterns.com/uploads/tr6QcnHiPCimDCVVc9oR3u/Captura_de_pantalla_2026-10-01_101655.png',
    alt: 'Carte imprimable aux roses aquarelle avec Romans 10:13',
    wide: true,
  },
  {
    id: 'globe',
    src: 'https://cdn.magicpatterns.com/uploads/jte1FeLhWRB8XWTDytDqrD/Captura_de_pantalla_2026-10-01_101633.png',
    alt: 'Carte illustrée de Jésus tenant le monde avec Jeremiah 31:3',
    wide: false,
  },
  {
    id: 'message',
    src: 'https://cdn.magicpatterns.com/uploads/fzgK1B8Lcapeik12eyzmw5/Captura_de_pantalla_2026-10-01_101612.png',
    alt: 'Carte « Dieu a un message pour vous » avec John 6:37',
    wide: true,
  },
  {
    id: 'lamb',
    src: 'https://cdn.magicpatterns.com/uploads/qTzEBhQYweNZ62U1g4mryi/Captura_de_pantalla_2026-10-01_101516.png',
    alt: 'Carte agneau aquarelle avec Psalm 23:3',
    wide: true,
  },
  {
    id: 'sign',
    src: 'https://cdn.magicpatterns.com/uploads/mWpwBQpPkKYQxv3Hvs6LyL/Captura_de_pantalla_2026-10-01_101438.png',
    alt: 'Carte style panneau routier avec John 3:16',
    wide: false,
  },
  {
    id: 'church-doors',
    src: 'https://cdn.magicpatterns.com/uploads/sxPfuxtX6vZpGkWbX3CALL/image.png',
    alt: 'Carte portes d’église et roses roses avec Proverbs 16:20',
    wide: true,
  },
  {
    id: 'flowered-cross',
    src: 'https://cdn.magicpatterns.com/uploads/7Ew6amTWLYbM4YPXav3NAN/image.png',
    alt: 'Carte croix en bois et fleurs blanches avec Titus 3:5',
    wide: true,
  },
  {
    id: 'floral-butterfly',
    src: 'https://cdn.magicpatterns.com/uploads/twpBhU5ATavAQHBi1wVDKa/image.png',
    alt: 'Carte fleurie en forme de papillon avec Matthew 11:28',
    wide: false,
  },
  {
    id: 'rose-hearts',
    src: 'https://cdn.magicpatterns.com/uploads/9ey6bq6fvo6zDazehePXxm/image.png',
    alt: 'Carte cœurs de roses roses avec Isaiah 41:10',
    wide: true,
  },
  {
    id: 'open-bibles',
    src: 'https://cdn.magicpatterns.com/uploads/t4q6wW8YtXY3AhKdGqfnXH/image.png',
    alt: 'Carte Bibles ouvertes avec Romans 5:1',
    wide: true,
  },
]

export const themes = ['L’amour de Dieu', 'L’espérance', 'La prière', 'La paix', 'Le salut', 'Jésus', 'La Croix', 'Les promesses de Dieu', 'L’encouragement', 'La foi']

/* ---------- Section 6 — Everyday evangelism ---------- */
export const places: Place[] = [
  { title: 'Au travail', text: 'Avec vos collègues ou les personnes que vous croisez au fil de la journée.' },
  { title: 'Dans votre quartier', text: 'Avec vos voisins et les habitants de votre quartier.' },
  { title: 'Avec vos cadeaux', text: 'Ajoutez une carte d’Évangile ou un verset biblique à un cadeau, un colis ou un geste de bonté.' },
  { title: 'Avec vos amis et votre famille', text: 'Partagez un message qui peut naturellement ouvrir la porte à une conversation plus profonde sur Jésus.' },
  { title: 'Lors des actions de votre église', text: 'Gardez à disposition des ressources bibliques prêtes à imprimer pour l’évangélisation, les actions de proximité et les activités de l’église.' },
  { title: 'Dans vos rencontres du quotidien', text: 'Gardez-en quelques-unes sur vous : quand une occasion se présente, vous avez déjà quelque chose de précieux à partager.' },
]

/* ---------- Section 7 — Shyness ---------- */
export const evangelismMyths = [
  'Ceux qui savent prêcher avec assurance.',
  'Ceux qui connaissent la Bible sur le bout des doigts.',
  'Ceux qui sont naturellement extravertis.',
  'Ceux qui savent toujours exactement quoi dire.',
]
export const firstSteps = ['Un verset.', 'Un message.', 'Un rappel de l’amour de Jésus.']

/* ---------- Section 8 — What you get ---------- */
export const featureHighlights: (Feature & { value: string })[] = [
  {
    value: '200+',
    title: 'Versets bibliques prêts à partager',
    text: 'Une vaste collection de messages centrés sur l’Écriture, choisis pour vous aider à partager la Parole de Dieu, l’espérance et l’Évangile dans les situations du quotidien.',
  },
  {
    value: '20+',
    title: 'Modèles imprimables différents pour partager l’Évangile',
    text: 'Plus de 20 modèles et formats différents pour partager l’Évangile, afin d’avoir plusieurs façons de transmettre l’Écriture selon la personne, le lieu ou l’occasion.',
  },
]
export const features: Feature[] = [
  { title: 'Plusieurs thèmes de l’Évangile', text: 'Des messages autour de l’espérance, du salut, de la prière, de l’amour de Dieu, de la foi, de l’encouragement, de Jésus, de la Croix et plus encore.' },
  { title: 'Fichiers PDF prêts à imprimer', text: 'Aucun logiciel de mise en page nécessaire. Choisissez simplement la ressource souhaitée, imprimez-la, découpez-la et partagez-la.' },
  { title: 'Réimprimez autant que nécessaire', text: 'Vos fichiers numériques restent à vous. Dès que vous en avez besoin, imprimez simplement une nouvelle série.' },
  { title: 'Accès numérique immédiat', text: 'Pas de livraison. Pas d’attente. Accédez à vos fichiers après votre achat et commencez à vous préparer dès aujourd’hui.' },
  {
    title: 'Mises à jour gratuites à vie',
    text: 'Votre pack continuera de s’enrichir. À mesure que nous créons de nouveaux modèles pour partager l’Évangile, ils sont ajoutés automatiquement à votre dossier —',
    strong: 'sans frais supplémentaires, à vie.',
  },
]

/* ---------- Section 9 — Purpose ---------- */
export const purposeUses = ['Pour être imprimé.', 'Pour être gardé sur vous.', 'Pour être partagé.']
export const peopleNeeds = [
  'Une parole d’espérance.',
  'Un rappel que Dieu les voit.',
  'Un verset biblique.',
  'Une invitation à l’église.',
  'Ou simplement quelqu’un prêt à semer la première graine.',
]

/* ---------- Section 10 — Bonuses ---------- */
export const bonuses: Bonus[] = [
  {
    number: 1,
    name: '50 amorces de conversation sur l’Évangile',
    lead: 'Vous ne savez pas quoi dire quand quelqu’un montre de l’intérêt ?',
    body: 'Utilisez ces amorces de conversation simples pour passer naturellement d’une discussion du quotidien à la foi, à Jésus et à l’Évangile.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/jY1ENULtbfX6p1WkWMpr1D/50_iniciadores_de_conversacion_gospel.png',
  },
  {
    number: 2,
    name: 'Petit guide de prière après le partage',
    lead: 'Vous avez semé la graine. Maintenant, priez pour elle.',
    body: 'Utilisez ce guide de prière simple pour prier pour les personnes que vous rencontrez, les messages que vous partagez et les graines que vous avez semées.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/28ZuiqdxhbyDgxeLoA9jyk/Guia_de_oracion_al_amanecer.png',
  },
  {
    number: 3,
    name: 'Kit d’invitation à l’église',
    lead: 'Des invitations imprimables prêtes à l’emploi pour proposer à quelqu’un une prochaine étape toute simple : venir à l’église.',
    body: 'Idéal pour inviter vos amis, voisins, collègues et les personnes que vous rencontrez lors de vos actions d’évangélisation.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/r9z4A2E8rHDpoQTX4N3tiq/3d406038-7340-4293-a6a1-1253bee7c13d.png',
  },
  {
    number: 4,
    name: 'Guide pour choisir le bon message',
    lead: 'Vous vous demandez quel message partager ?',
    body: 'Utilisez ce guide de référence rapide pour choisir un message de l’Évangile adapté à ce que traverse la personne — espérance, peur, découragement, prière, amour de Dieu, salut et plus encore.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/76NzgMaHmTk8ni1CZ8BH8v/Guia_de_mensajes_del_evangelio.png',
  },
  {
    number: 5,
    name: 'Défi de 30 jours pour partager l’Évangile',
    lead: 'Faites de l’évangélisation non plus quelque chose que vous repoussez sans cesse, mais quelque chose que vous pratiquez avec intention.',
    body: 'Suivez un défi simple de 30 jours conçu pour vous encourager à chercher les occasions, à semer et à faire du partage de l’Évangile une part de votre vie chrétienne au quotidien.',
    coverImage: 'https://cdn.magicpatterns.com/uploads/cF6WoDm6AChS8K46fjqF6j/Desafio_Gospel_de_30_Dias.png',
  },
]

/* ---------- Section 11 — Reframe ---------- */
export const ordinaryPlaces = [
  { place: 'Le supermarché', rest: 'n’est plus seulement le supermarché.' },
  { place: 'Votre lieu de travail', rest: 'n’est plus seulement votre lieu de travail.' },
  { place: 'Votre quartier', rest: 'n’est plus seulement l’endroit où vous habitez.' },
]
export const someoneMay = [
  'Quelqu’un prie peut-être pour retrouver l’espérance.',
  'Quelqu’un se sent peut-être oublié.',
  'Quelqu’un cherche peut-être Dieu.',
  'Quelqu’un a peut-être besoin d’entendre parler de Jésus.',
]

/* ---------- Section 12 — Social proof ---------- */
export const testimonials: Testimonial[] = [
  {
    headline: 'J’ai enfin arrêté de trop réfléchir à la façon de commencer.',
    quote:
      'Je voulais partager ma foi plus souvent, mais je me figeais toujours parce que je ne savais pas quoi dire. Avoir ces messages déjà préparés m’a vraiment facilité ce premier pas. J’en ai imprimé quelques-uns, je les ai glissés dans mon sac à main, et maintenant je cherche vraiment des occasions de les partager.',
    name: 'Sarah M.',
    role: 'Maman chrétienne',
  },
  {
    headline: 'Maintenant, j’ai toujours quelque chose à partager sur moi.',
    quote:
      'J’ai imprimé plusieurs modèles et je garde une petite pile dans ma voiture et une autre dans mon sac. J’aime savoir que si je rencontre quelqu’un qui a besoin d’encouragement, j’ai déjà un verset biblique prêt à lui offrir. C’est une façon si simple de partager la Parole de Dieu de manière plus intentionnelle.',
    name: 'Rebecca T.',
    role: 'Membre d’église',
  },
  {
    headline: 'Nous les utilisons pour l’évangélisation et pour nos cadeaux.',
    quote:
      'Ils nous ont été tellement utiles pour les actions d’évangélisation de notre église. Nous les avons glissés dans de petits cadeaux, distribués lors d’activités de quartier et partagés avec les visiteurs. Ils permettent d’ajouter facilement un message de l’Évangile qui a du sens, sans devoir tout créer nous-mêmes.',
    name: 'Daniel R.',
    role: 'Bénévole en ministère',
  },
  {
    headline: 'Il y a toujours un message adapté au moment.',
    quote:
      'Ce qui m’a le plus surprise, c’est la variété. Avec plus de 200 versets bibliques et plus de 20 modèles différents, je ne donne pas exactement le même message à tout le monde. Je peux choisir quelque chose sur l’espérance, la prière, l’amour de Dieu, la foi ou le salut selon la personne et la situation.',
    name: 'Michelle A.',
    role: 'Responsable du ministère des femmes',
  },
]

/* ---------- Section 13 — Transition ---------- */
export const noNeeds = [
  'Vous n’avez pas besoin de devenir prédicateur de rue du jour au lendemain.',
  'Vous n’avez pas besoin d’apprendre par cœur le discours parfait.',
  'Vous n’avez pas besoin d’avoir réponse à tout.',
]
export const oneSeed = ['Un verset.', 'Un message.', 'Une personne.', 'Une graine.']

/* ---------- Section 14 — Complete offer ---------- */
export const offerIncludes = [
  '200+ versets bibliques prêts à partager',
  '20+ modèles imprimables différents pour partager l’Évangile',
  'Des messages sur l’espérance, le salut, la prière, l’amour de Dieu, la foi, Jésus et plus encore',
  'Fichiers PDF prêts à imprimer',
  'Réimpression possible chaque fois que vous en avez besoin',
  'Accès numérique immédiat',
  'Mises à jour gratuites à vie — nouveaux modèles ajoutés au fur et à mesure',
]

/** Real printables used in the offer collage. */
export const collagePrintables: CollageItem[] = [
  { src: 'https://cdn.magicpatterns.com/uploads/ntaKAtoqbqdciapmGtrWpd/Captura_de_pantalla_2026-10-01_101655.png', alt: 'Carte roses aquarelle avec Romans 10:13' },
  { src: 'https://cdn.magicpatterns.com/uploads/bzdhvDa8EmftVLKUM3w9j7/Captura_de_pantalla_2026-10-01_104603.png', alt: 'Carte papillon bleu avec 1 John 4:10' },
  { src: 'https://cdn.magicpatterns.com/uploads/xwCeiSA3Pzg9vqLdzEr1TT/Captura_de_pantalla_2026-10-01_101612.png', alt: 'Carte « Dieu a un message pour vous » avec John 6:37' },
  { src: 'https://cdn.magicpatterns.com/uploads/jAKdNV2DNkuHubQ2J68sNX/Captura_de_pantalla_2026-10-01_101633.png', alt: 'Carte Jésus tenant le monde avec Jeremiah 31:3' },
  { src: 'https://cdn.magicpatterns.com/uploads/gkS7aRXwDmdUh6h7MM6Kt1/Captura_de_pantalla_2026-10-01_104714.png', alt: 'Carte Bible bleue avec Psalm 18' },
  { src: 'https://cdn.magicpatterns.com/uploads/syHCa192HNoiVTwMQGsCQ5/Captura_de_pantalla_2026-10-01_104641.png', alt: 'Carte mains en prière avec Philippians 4:6' },
  { src: 'https://cdn.magicpatterns.com/uploads/hLKe6dVbQH9TBjVRuLnvuz/Captura_de_pantalla_2026-10-01_101516.png', alt: 'Carte agneau aquarelle avec Psalm 23:3' },
  { src: 'https://cdn.magicpatterns.com/uploads/rJQXUMn1yCFV8gNhFR7vUZ/Captura_de_pantalla_2026-10-01_104917.png', alt: 'Carte Jésus avec John 3:16' },
  { src: 'https://cdn.magicpatterns.com/uploads/7ahiHGS4wgvEsYByb6xSA6/Captura_de_pantalla_2026-10-01_104816.png', alt: 'Carte roses rouges avec Isaiah 45:22' },
]

/* ---------- Section 16 — FAQ ---------- */
export const faqs: FaqItem[] = [
  { question: 'Est-ce un produit physique ?', answer: 'Non.\n\nIl s’agit d’un produit 100 % numérique. Vous recevrez des fichiers PDF que vous pourrez télécharger et imprimer vous-même.' },
  { question: 'Comment vais-je recevoir le pack ?', answer: 'Dès votre achat finalisé, vous recevrez un accès numérique immédiat à vos ressources.\n\nAucune livraison physique n’est effectuée.' },
  { question: 'Que contient exactement le pack ?', answer: 'Vous recevrez 200+ versets bibliques répartis sur 20+ modèles imprimables différents pour partager l’Évangile, ainsi que les 5 bonus d’évangélisation inclus.' },
  { question: 'Les fichiers sont-ils prêts à imprimer ?', answer: 'Oui.\n\nLes ressources sont déjà conçues et préparées au format PDF : il vous suffit d’imprimer, de découper et de commencer à partager.' },
  { question: 'Puis-je modifier les modèles ?', answer: 'Non.\n\nLes modèles ne sont pas modifiables. Ils sont livrés finalisés et prêts à imprimer, pour que vous n’ayez pas à perdre de temps à concevoir ou à modifier quoi que ce soit.' },
  { question: 'Puis-je les imprimer plusieurs fois ?', answer: 'Oui.\n\nUne fois vos fichiers reçus, vous pouvez imprimer de nouveaux exemplaires chaque fois que vous en avez besoin, pour votre partage personnel de l’Évangile et pour un usage autorisé dans le cadre d’un ministère.' },
  { question: 'Ai-je besoin d’un logiciel spécial ?', answer: 'Non.\n\nIl vous suffit d’un appareil capable d’ouvrir des fichiers PDF et d’un accès à une imprimante ou à une imprimerie.' },
  {
    question: 'Est-ce réservé aux pasteurs ou aux responsables d’église ?',
    answer:
      'Non.\n\nLe Pack Complet d’Évangélisation a été créé pour les chrétiens ordinaires qui veulent une façon simple de commencer à partager leur foi. Vous n’avez pas besoin d’être pasteur, missionnaire ou évangéliste expérimenté.',
  },
  {
    question: 'Et si je suis timide ou nerveux à l’idée d’évangéliser ?',
    answer:
      'C’est justement pour cela que ces ressources peuvent vous être si utiles.\n\nVous n’avez pas besoin de commencer par une longue conversation ni de savoir exactement quoi dire. Commencez petit. Partagez un verset. Offrez un message de l’Évangile. Semez une graine.',
  },
  {
    question: 'Les églises peuvent-elles utiliser ces ressources pour l’évangélisation ?',
    answer: 'Ces ressources peuvent être utiles pour l’évangélisation personnelle, les activités de ministère et les actions de proximité, conformément aux droits d’utilisation inclus avec votre achat.',
  },
  {
    question: 'Quels types de versets et de messages sont inclus ?',
    answer:
      'Vous trouverez des messages centrés sur l’Écriture autour de Jésus, du salut, de l’amour de Dieu, de l’espérance, de la prière, de la paix, de l’encouragement, de la foi, des promesses de Dieu, de la Croix et plus encore.\n\nAvec 200+ versets bibliques et 20+ modèles différents, vous aurez des messages variés pour des personnes et des situations différentes.',
  },
  { question: 'Y a-t-il une garantie ?', answer: 'Oui.\n\nVotre achat est protégé par une garantie de 7 jours.' },
  {
    question: 'Vais-je aussi recevoir les futurs modèles ?',
    answer:
      'Oui. À mesure que nous créons de nouveaux modèles pour partager l’Évangile, ils sont ajoutés automatiquement à votre dossier. Si vous avez déjà acheté le pack, ces futures mises à jour sont incluses gratuitement, à vie.',
  },
]

/* ---------- Section 17 — Final close ---------- */
export const alreadyGo = ['Vous allez déjà au supermarché.', 'Vous allez déjà au travail.', 'Vous croisez déjà vos voisins.', 'Vous rencontrez déjà des gens.']
